import React, { useState, useEffect, useCallback } from "react";
import "../../Styles/SettingsPage.css";
import { staffAPI, settingsAPI } from "../../api/index"; // adjust path to match your project

const SettingsPage = () => {
  // ---------- Profile (UI-only — no backend endpoint provided) ----------
  const [profileData, setProfileData] = useState({
    name: "INR MLA",
    constituency: "Kandukur",
    email: "mla.kandukur@example.gov.in",
    phone: "9876543210",
    office: "MLA Camp Office, Kandukur",
  });

  // ---------- Notifications (UI-only — no backend endpoint provided) ----------
  const [notifications, setNotifications] = useState({
    newBeneficiary: true,
    followUpReminders: true,
    eventReminders: true,
    smsAlerts: false,
    emailReports: true,
  });

  // ---------- Two-Factor (UI-only — no backend endpoint provided) ----------
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(true);

  // ---------- STAFF (real API: /staff, super_admin only) ----------
  const [staffData, setStaffData] = useState([]);
  const [staffLoading, setStaffLoading] = useState(true);
  const [staffLoadError, setStaffLoadError] = useState("");
  const [staffSearch, setStaffSearch] = useState("");

  // ---------- CONSTITUENCY INFO (real API: /settings) ----------
  const [constituencyInfo, setConstituencyInfo] = useState(null);
  const [constLoading, setConstLoading] = useState(true);
  const [constLoadError, setConstLoadError] = useState("");
  const [savingConst, setSavingConst] = useState(false);

  // ---------- Staff Modal (Add / Edit) ----------
  const [isStaffModalOpen, setIsStaffModalOpen] = useState(false);
  const [staffModalMode, setStaffModalMode] = useState("add");
  const [staffForm, setStaffForm] = useState({
    id: null,
    name: "",
    email: "",
    password: "",
    role: "admin",
    mobile: "",
    status: "active",
  });
  const [savingStaff, setSavingStaff] = useState(false);
  const [staffFormError, setStaffFormError] = useState("");

  // ---------- Delete Staff Confirm ----------
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deletingStaff, setDeletingStaff] = useState(false);

  // ---------- Change Password Modal (UI-only — no backend endpoint provided) ----------
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: "",
    newPass: "",
    confirm: "",
  });

  // ---------- Edit Constituency Modal ----------
  const [showConstModal, setShowConstModal] = useState(false);
  const [constForm, setConstForm] = useState(null);

  const initialsOf = (name) =>
    (name || "")
      .split(" ")
      .filter(Boolean)
      .map((w) => w[0])
      .join("")
      .toUpperCase()
      .slice(0, 2) || "?";

  const avatarColors = [
    "sett123-avatar-blue",
    "sett123-avatar-pink",
    "sett123-avatar-orange",
    "sett123-avatar-green",
    "sett123-avatar-purple",
  ];

  const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

  // ============================================================
  // FETCH: staff + settings
  // ============================================================
  const fetchStaff = useCallback(async () => {
    setStaffLoading(true);
    setStaffLoadError("");
    try {
      const res = await staffAPI.getAll({});
      const data = Array.isArray(res.data) ? res.data : res.data?.items || [];
      setStaffData(data);
    } catch (err) {
      console.error("Failed to load staff:", err);
      setStaffLoadError(
        err.response?.data?.detail || "Could not load staff accounts (super admin only)."
      );
    } finally {
      setStaffLoading(false);
    }
  }, []);

  const fetchSettings = useCallback(async () => {
    setConstLoading(true);
    setConstLoadError("");
    try {
      const res = await settingsAPI.get();
      setConstituencyInfo(res.data);
    } catch (err) {
      console.error("Failed to load settings:", err);
      setConstLoadError(err.response?.data?.detail || "Could not load constituency settings.");
    } finally {
      setConstLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStaff();
    fetchSettings();
  }, [fetchStaff, fetchSettings]);

  const handleProfileChange = (e) => {
    setProfileData({ ...profileData, [e.target.name]: e.target.value });
  };

  const toggleNotification = (key) => {
    setNotifications({ ...notifications, [key]: !notifications[key] });
  };

  // ============================================================
  // STAFF LOGIC (real API)
  // ============================================================
  const openAddStaffModal = () => {
    setStaffModalMode("add");
    setStaffForm({ id: null, name: "", email: "", password: "", role: "admin", mobile: "", status: "active" });
    setStaffFormError("");
    setIsStaffModalOpen(true);
  };

  const openEditStaffModal = (staff) => {
    setStaffModalMode("edit");
    setStaffForm({
      id: staff.id,
      name: staff.name || "",
      email: staff.email || "",
      password: "", // never pre-filled; only sent if the user types a new one
      role: staff.role || "admin",
      mobile: staff.mobile || "",
      status: staff.status || "active",
    });
    setStaffFormError("");
    setIsStaffModalOpen(true);
  };

  const closeStaffModal = () => {
    setIsStaffModalOpen(false);
  };

  const handleStaffFormChange = (e) => {
    const { name, value } = e.target;
    setStaffForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleStaffSubmit = async (e) => {
    e.preventDefault();
    setStaffFormError("");

    if (!staffForm.name || !staffForm.email || !staffForm.mobile) {
      setStaffFormError("Please fill all required fields.");
      return;
    }
    if (staffModalMode === "add" && (!staffForm.password || staffForm.password.length < 8)) {
      setStaffFormError("Password is required and must be at least 8 characters.");
      return;
    }
    if (staffModalMode === "edit" && staffForm.password && staffForm.password.length < 8) {
      setStaffFormError("New password must be at least 8 characters.");
      return;
    }

    setSavingStaff(true);
    try {
      if (staffModalMode === "add") {
        await staffAPI.create({
          name: staffForm.name,
          email: staffForm.email,
          password: staffForm.password,
          role: staffForm.role,
          mobile: staffForm.mobile,
          status: staffForm.status,
        });
      } else {
        const payload = {
          name: staffForm.name,
          email: staffForm.email,
          role: staffForm.role,
          mobile: staffForm.mobile,
          status: staffForm.status,
        };
        if (staffForm.password) payload.password = staffForm.password;
        await staffAPI.update(staffForm.id, payload);
      }

      await fetchStaff();
      setIsStaffModalOpen(false);
    } catch (err) {
      console.error("Failed to save staff:", err);
      setStaffFormError(
        err.response?.data?.detail || "Something went wrong while saving this staff account."
      );
    } finally {
      setSavingStaff(false);
    }
  };

  const openDeleteModal = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteId(null);
  };

  const confirmDeleteStaff = async () => {
    setDeletingStaff(true);
    try {
      await staffAPI.delete(deleteId);
      await fetchStaff();
      closeDeleteModal();
    } catch (err) {
      console.error("Failed to delete staff:", err);
      alert(
        err.response?.data?.detail ||
          "Could not remove this staff member (super admin only; you cannot delete your own account)."
      );
    } finally {
      setDeletingStaff(false);
    }
  };

  // ---------- Staff search filter (client-side) ----------
  const filteredStaff = staffData.filter((s) => {
    const q = staffSearch.trim().toLowerCase();
    if (q === "") return true;
    return (
      (s.name || "").toLowerCase().includes(q) ||
      (s.role || "").toLowerCase().includes(q) ||
      (s.mobile || "").includes(q) ||
      (s.status || "").toLowerCase().includes(q) ||
      (s.email || "").toLowerCase().includes(q)
    );
  });

  // ============================================================
  // PASSWORD LOGIC (UI-only — no backend endpoint provided)
  // ============================================================
  const openPasswordModal = () => {
    setPasswordForm({ current: "", newPass: "", confirm: "" });
    setShowPasswordModal(true);
  };

  const closePasswordModal = () => {
    setShowPasswordModal(false);
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordForm((prev) => ({ ...prev, [name]: value }));
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();

    if (!passwordForm.current || !passwordForm.newPass || !passwordForm.confirm) {
      alert("Please fill all password fields");
      return;
    }

    if (passwordForm.newPass !== passwordForm.confirm) {
      alert("New password and confirm password do not match");
      return;
    }

    // NOTE: no /auth/change-password (or similar) endpoint exists in the
    // provided API — this is a UI-only placeholder until one is added.
    alert("Password updated successfully");
    closePasswordModal();
  };

  // ============================================================
  // CONSTITUENCY INFO LOGIC (real API)
  // ============================================================
  const openConstModal = () => {
    setConstForm({ ...constituencyInfo });
    setShowConstModal(true);
  };

  const closeConstModal = () => {
    setShowConstModal(false);
  };

  const handleConstFormChange = (e) => {
    const { name, value } = e.target;
    setConstForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleConstSubmit = async (e) => {
    e.preventDefault();
    setSavingConst(true);
    try {
      const res = await settingsAPI.update({
        constituency_name: constForm.constituency_name || null,
        constituency_no: constForm.constituency_no || null,
        state: constForm.state || null,
        district: constForm.district || null,
        lok_sabha_constituency: constForm.lok_sabha_constituency || null,
        current_mla: constForm.current_mla || null,
        year_established: constForm.year_established ? Number(constForm.year_established) : null,
        total_mandals: constForm.total_mandals ? Number(constForm.total_mandals) : null,
        total_villages: constForm.total_villages ? Number(constForm.total_villages) : null,
        total_population: constForm.total_population ? Number(constForm.total_population) : null,
        office_address: constForm.office_address || null,
        contact_email: constForm.contact_email || null,
        contact_phone: constForm.contact_phone || null,
      });
      setConstituencyInfo(res.data);
      setShowConstModal(false);
    } catch (err) {
      console.error("Failed to update settings:", err);
      alert(err.response?.data?.detail || "Something went wrong while saving constituency info.");
    } finally {
      setSavingConst(false);
    }
  };

  return (
    <div className="sett123-page">
      {/* Header */}
      <div className="sett123-header">
        <h2 className="sett123-title">Settings</h2>
        <p className="sett123-subtitle">
          Manage your profile, account, team and preferences
        </p>
      </div>

      {/* Profile Card (UI-only, unwired) */}
      <div className="sett123-card sett123-profile-card">
        <div className="sett123-card-title-row">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.7" />
            <path d="M4 20c0-3.5 3.6-6 8-6s8 2.5 8 6" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
          <h3>Profile Information</h3>

          <button className="sett123-change-photo-btn">
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path d="M4 8a2 2 0 012-2h1l1.5-2h7L17 6h1a2 2 0 012 2v10a2 2 0 01-2 2H6a2 2 0 01-2-2V8z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
              <circle cx="12" cy="13" r="3.5" stroke="currentColor" strokeWidth="1.7" />
            </svg>
            Change Photo
          </button>
        </div>

        <div className="sett123-profile-body">
          <div className="sett123-profile-photo-col">
            <div className="sett123-profile-photo">INR</div>
            <span className="sett123-profile-name">{profileData.name}</span>
            <span className="sett123-profile-tag">{profileData.constituency} Constituency</span>
          </div>

          <div className="sett123-profile-form-col">
            <div className="sett123-form-grid">
              <div className="sett123-form-group">
                <label>Full Name</label>
                <div className="sett123-input-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <circle cx="12" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
                    <path d="M4 20c0-3.3 3.4-5.5 8-5.5s8 2.2 8 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                  </svg>
                  <input
                    type="text"
                    name="name"
                    value={profileData.name}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>

              <div className="sett123-form-group">
                <label>Constituency</label>
                <div className="sett123-input-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M4 21V9l8-6 8 6v12M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="text"
                    name="constituency"
                    value={profileData.constituency}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>

              <div className="sett123-form-group">
                <label>Email Address</label>
                <div className="sett123-input-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
                    <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="email"
                    name="email"
                    value={profileData.email}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>

              <div className="sett123-form-group">
                <label>Phone Number</label>
                <div className="sett123-input-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M22 16.9v3a2 2 0 01-2.2 2 19.8 19.8 0 01-8.6-3.1 19.5 19.5 0 01-6-6A19.8 19.8 0 012.1 4.2 2 2 0 014.1 2h3a2 2 0 012 1.7c.1.9.3 1.8.6 2.7a2 2 0 01-.5 2.1L8 9.7a16 16 0 006 6l1.2-1.2a2 2 0 012.1-.5c.9.3 1.8.5 2.7.6a2 2 0 011.7 2z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="tel"
                    name="phone"
                    value={profileData.phone}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>

              <div className="sett123-form-group sett123-full-width">
                <label>Office Address</label>
                <div className="sett123-input-icon">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                    <path d="M3 21h18M6 21V5a1 1 0 011-1h10a1 1 0 011 1v16M9 9h1M9 13h1M14 9h1M14 13h1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <input
                    type="text"
                    name="office"
                    value={profileData.office}
                    onChange={handleProfileChange}
                  />
                </div>
              </div>
            </div>

            <button className="sett123-save-btn" type="button" title="No profile-update endpoint exists yet">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              Save Changes
            </button>
          </div>
        </div>
      </div>

      {/* Two-column row: Security / Staff */}
      <div className="sett123-row-grid">
        {/* Account & Security (mostly UI-only, unwired) */}
        <div className="sett123-card sett123-security-card">
          <div className="sett123-card-title-row">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M12 2l8 4v6c0 5-3.4 8.7-8 10-4.6-1.3-8-5-8-10V6l8-4z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            </svg>
            <h3>Account &amp; Security</h3>
          </div>

          <button className="sett123-security-item" onClick={openPasswordModal}>
            <div className="sett123-security-icon sett123-sec-icon-green">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" strokeWidth="1.7" />
              </svg>
            </div>
            <div className="sett123-security-text">
              <span className="sett123-security-label">Change Password</span>
              <span className="sett123-security-desc">Update your account password</span>
            </div>
            <svg className="sett123-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="sett123-security-item sett123-security-item-static">
            <div className="sett123-security-icon sett123-sec-icon-orange">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M8 21h8M12 18v3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </div>
            <div className="sett123-security-text">
              <span className="sett123-security-label">Two-Factor Authentication</span>
              <span className="sett123-security-desc">Add an extra layer of security</span>
            </div>
            <label className="sett123-switch">
              <input
                type="checkbox"
                checked={twoFactorEnabled}
                onChange={() => setTwoFactorEnabled((prev) => !prev)}
              />
              <span className="sett123-slider"></span>
            </label>
          </div>

          <div className="sett123-security-item sett123-security-item-static">
            <div className="sett123-security-icon sett123-sec-icon-blue">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="16" height="12" rx="2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M22 8l-4 3 4 3V8z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="sett123-security-text">
              <span className="sett123-security-label">Login Sessions</span>
              <span className="sett123-security-desc">Manage your active sessions</span>
            </div>
            <svg className="sett123-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <div className="sett123-security-item sett123-security-item-static">
            <div className="sett123-security-icon sett123-sec-icon-red">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="sett123-security-text">
              <span className="sett123-security-label">Delete Account</span>
              <span className="sett123-security-desc">Permanently delete your account</span>
            </div>
            <svg className="sett123-chevron" width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>

          <div className="sett123-secure-note">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
              <path d="M8.5 12.5l2.5 2.5 4.5-5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <div>
              <strong>Your account is secure</strong>
              <p>Last login: 19 July 2026, 10:15 AM</p>
            </div>
          </div>
        </div>

        {/* Staff Management (real API) */}
        <div className="sett123-card sett123-staff-card">
          <div className="sett123-card-header-row">
            <div className="sett123-card-title-row">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
                <circle cx="17" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.7" />
                <path d="M15.5 14.7c2.6.4 4.5 2.3 4.5 5.3" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
              <h3>Staff Management</h3>
            </div>

            <button className="sett123-add-staff-btn" onClick={openAddStaffModal}>
              + Add Staff
            </button>
          </div>

          {staffLoadError && <div className="sett123-staff-empty">{staffLoadError}</div>}

          <div className="sett123-staff-search-box">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
              <path d="M21 21l-4-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
            <input
              type="text"
              placeholder="Search by name, role, mobile, email or status..."
              value={staffSearch}
              onChange={(e) => setStaffSearch(e.target.value)}
            />
          </div>

          <div className="sett123-staff-table-wrapper">
            <table className="sett123-staff-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Role</th>
                  <th>Mobile</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {staffLoading ? (
                  <tr>
                    <td colSpan="5" className="sett123-staff-empty">Loading...</td>
                  </tr>
                ) : filteredStaff.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="sett123-staff-empty">
                      No staff members found.
                    </td>
                  </tr>
                ) : (
                  filteredStaff.map((staff, index) => (
                    <tr key={staff.id}>
                      <td>
                        <div className="sett123-staff-name-cell">
                          <div className={`sett123-avatar ${avatarColors[index % avatarColors.length]}`}>
                            {initialsOf(staff.name)}
                          </div>
                          <div>
                            <div>{staff.name}</div>
                            <div className="sett123-staff-email-sub">{staff.email}</div>
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="sett123-role-badge">
                          {staff.role === "super_admin" ? "Super Admin" : "Admin"}
                        </span>
                      </td>
                      <td>{staff.mobile || "—"}</td>
                      <td>
                        <span
                          className={`sett123-staff-status ${
                            staff.status === "active"
                              ? "sett123-staff-active"
                              : "sett123-staff-inactive"
                          }`}
                        >
                          {capitalize(staff.status)}
                        </span>
                      </td>
                      <td className="sett123-staff-action-cell">
                        <button
                          className="sett123-edit-btn"
                          onClick={() => openEditStaffModal(staff)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                              stroke="currentColor"
                              strokeWidth="1.9"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                        <button
                          className="sett123-delete-btn"
                          onClick={() => openDeleteModal(staff.id)}
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
                            <path
                              d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                              stroke="currentColor"
                              strokeWidth="1.9"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Notifications (UI-only, unwired) */}
      <div className="sett123-card sett123-notif-card-full">
        <div className="sett123-card-title-row">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
            <path d="M6 8a6 6 0 1112 0c0 5 2 6 2 6H4s2-1 2-6z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
            <path d="M9.5 20a2.5 2.5 0 005 0" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
          </svg>
          <h3>Notifications</h3>
        </div>

        <div className="sett123-notif-grid">
          <div className="sett123-notif-item">
            <div className="sett123-notif-icon sett123-notif-icon-blue">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </div>
            <div className="sett123-notif-text">
              <span className="sett123-notif-label">New Beneficiary Added</span>
              <span className="sett123-notif-desc">Get notified when a new beneficiary is added</span>
            </div>
            <label className="sett123-switch">
              <input
                type="checkbox"
                checked={notifications.newBeneficiary}
                onChange={() => toggleNotification("newBeneficiary")}
              />
              <span className="sett123-slider"></span>
            </label>
          </div>

          <div className="sett123-notif-item">
            <div className="sett123-notif-icon sett123-notif-icon-green">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.7" />
                <path d="M12 7v5l3.5 2" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="sett123-notif-text">
              <span className="sett123-notif-label">Follow-up Reminders</span>
              <span className="sett123-notif-desc">Receive follow-up and due date reminders</span>
            </div>
            <label className="sett123-switch">
              <input
                type="checkbox"
                checked={notifications.followUpReminders}
                onChange={() => toggleNotification("followUpReminders")}
              />
              <span className="sett123-slider"></span>
            </label>
          </div>

          <div className="sett123-notif-item">
            <div className="sett123-notif-icon sett123-notif-icon-purple">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="5" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M3 10h18M8 3v4M16 3v4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" />
              </svg>
            </div>
            <div className="sett123-notif-text">
              <span className="sett123-notif-label">Event Reminders</span>
              <span className="sett123-notif-desc">Get notified about upcoming events</span>
            </div>
            <label className="sett123-switch">
              <input
                type="checkbox"
                checked={notifications.eventReminders}
                onChange={() => toggleNotification("eventReminders")}
              />
              <span className="sett123-slider"></span>
            </label>
          </div>

          <div className="sett123-notif-item">
            <div className="sett123-notif-icon sett123-notif-icon-gray">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="5" width="20" height="14" rx="2" stroke="currentColor" strokeWidth="1.7" />
                <path d="M2 7l10 6 10-6" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="sett123-notif-text">
              <span className="sett123-notif-label">SMS Alerts</span>
              <span className="sett123-notif-desc">Receive important SMS alerts</span>
            </div>
            <label className="sett123-switch">
              <input
                type="checkbox"
                checked={notifications.smsAlerts}
                onChange={() => toggleNotification("smsAlerts")}
              />
              <span className="sett123-slider"></span>
            </label>
          </div>

          <div className="sett123-notif-item">
            <div className="sett123-notif-icon sett123-notif-icon-blue">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="5" width="18" height="14" rx="2" stroke="currentColor" strokeWidth="1.6" />
                <path d="M3 7l9 6 9-6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg>
            </div>
            <div className="sett123-notif-text">
              <span className="sett123-notif-label">Email Reports</span>
              <span className="sett123-notif-desc">Receive daily/weekly email reports</span>
            </div>
            <label className="sett123-switch">
              <input
                type="checkbox"
                checked={notifications.emailReports}
                onChange={() => toggleNotification("emailReports")}
              />
              <span className="sett123-slider"></span>
            </label>
          </div>
        </div>

        <div className="sett123-notif-footer">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.8" />
            <path d="M12 16v-5M12 8h.01" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
          </svg>
          You can update your preferences at any time.
        </div>
      </div>

      {/* Constituency Info (real API) */}
      <div className="sett123-card sett123-constituency-card">
        <div className="sett123-constituency-row">
          <div className="sett123-card-title-row">
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none">
              <path d="M4 21V9l8-6 8 6v12M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
            </svg>
            <h3>Constituency Information</h3>
          </div>

          <button className="sett123-edit-info-btn" onClick={openConstModal} disabled={constLoading}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
              <path
                d="M12 20h9M16.5 3.5a2.1 2.1 0 013 3L7 19l-4 1 1-4 12.5-12.5z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Edit Info
          </button>
        </div>

        {constLoadError && <div className="sett123-staff-empty">{constLoadError}</div>}

        {constLoading ? (
          <div className="sett123-staff-empty">Loading...</div>
        ) : (
          <div className="sett123-constituency-stats">
            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M4 21V9l8-6 8 6v12M9 21v-6h6v6" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
              </svg>
              <div>
                <span className="sett123-const-label">Constituency</span>
                <span className="sett123-const-value">{constituencyInfo?.constituency_name || "—"}</span>
              </div>
            </div>

            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="1.6" />
                <path d="M3 9h18M9 4v16" stroke="currentColor" strokeWidth="1.6" />
              </svg>
              <div>
                <span className="sett123-const-label">District</span>
                <span className="sett123-const-value">{constituencyInfo?.district || "—"}</span>
              </div>
            </div>

            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
                <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <circle cx="17" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.6" />
                <path d="M15.5 14.7c2.6.4 4.5 2.3 4.5 5.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <div>
                <span className="sett123-const-label">Total Mandals</span>
                <span className="sett123-const-value">{constituencyInfo?.total_mandals ?? "—"}</span>
              </div>
            </div>

            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M3 21h18M5 21V7l7-4 7 4v14M9 9h1M9 13h1M14 9h1M14 13h1" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <span className="sett123-const-label">Total Villages</span>
                <span className="sett123-const-value">{constituencyInfo?.total_villages ?? "—"}</span>
              </div>
            </div>

            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="9" cy="8" r="3.2" stroke="currentColor" strokeWidth="1.6" />
                <path d="M3 20c0-3.3 2.7-5.5 6-5.5s6 2.2 6 5.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                <circle cx="17" cy="8" r="2.6" stroke="currentColor" strokeWidth="1.6" />
                <path d="M15.5 14.7c2.6.4 4.5 2.3 4.5 5.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
              </svg>
              <div>
                <span className="sett123-const-label">Total Population</span>
                <span className="sett123-const-value">
                  {constituencyInfo?.total_population != null
                    ? constituencyInfo.total_population.toLocaleString()
                    : "—"}
                </span>
              </div>
            </div>

            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <path d="M3 21h18M6 21V5a1 1 0 011-1h10a1 1 0 011 1v16" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
              <div>
                <span className="sett123-const-label">State</span>
                <span className="sett123-const-value">{constituencyInfo?.state || "—"}</span>
              </div>
            </div>

            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.6" />
              </svg>
              <div>
                <span className="sett123-const-label">Lok Sabha Constituency</span>
                <span className="sett123-const-value">{constituencyInfo?.lok_sabha_constituency || "—"}</span>
              </div>
            </div>

            <div className="sett123-const-stat">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="8" r="3.5" stroke="currentColor" strokeWidth="1.6" />
              </svg>
              <div>
                <span className="sett123-const-label">Current MLA</span>
                <span className="sett123-const-value">{constituencyInfo?.current_mla || "—"}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* ---------------- ADD / EDIT STAFF MODAL ---------------- */}
      {isStaffModalOpen && (
        <div className="sett123-modal-overlay" onClick={closeStaffModal}>
          <div className="sett123-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="sett123-modal-header">
              <h3>{staffModalMode === "add" ? "Add New Staff Member" : "Edit Staff Member"}</h3>
              <button className="sett123-modal-close-btn" onClick={closeStaffModal}>
                &times;
              </button>
            </div>

            <form onSubmit={handleStaffSubmit}>
              <div className="sett123-modal-body">
                {staffFormError && <div className="sett123-form-error">{staffFormError}</div>}

                <div className="sett123-form-group">
                  <label>Staff Name *</label>
                  <input
                    type="text"
                    name="name"
                    placeholder="Enter Full Name"
                    value={staffForm.name}
                    onChange={handleStaffFormChange}
                    required
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Email *</label>
                  <input
                    type="email"
                    name="email"
                    placeholder="Enter Email Address"
                    value={staffForm.email}
                    onChange={handleStaffFormChange}
                    required
                  />
                </div>

                <div className="sett123-form-group">
                  <label>
                    {staffModalMode === "add" ? "Password *" : "New Password (leave blank to keep current)"}
                  </label>
                  <input
                    type="password"
                    name="password"
                    placeholder={staffModalMode === "add" ? "At least 8 characters" : "Only fill in to change"}
                    value={staffForm.password}
                    onChange={handleStaffFormChange}
                    required={staffModalMode === "add"}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Role *</label>
                  <select name="role" value={staffForm.role} onChange={handleStaffFormChange}>
                    <option value="admin">Admin</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>

                <div className="sett123-form-group">
                  <label>Mobile Number *</label>
                  <input
                    type="tel"
                    name="mobile"
                    placeholder="Enter Mobile Number"
                    maxLength="10"
                    value={staffForm.mobile}
                    onChange={handleStaffFormChange}
                    required
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Status *</label>
                  <select name="status" value={staffForm.status} onChange={handleStaffFormChange}>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>

              <div className="sett123-modal-footer">
                <button type="button" className="sett123-modal-cancel-btn" onClick={closeStaffModal} disabled={savingStaff}>
                  Cancel
                </button>
                <button type="submit" className="sett123-modal-submit-btn" disabled={savingStaff}>
                  {savingStaff ? "Saving..." : staffModalMode === "add" ? "Add Staff" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- DELETE STAFF CONFIRM MODAL ---------------- */}
      {showDeleteModal && (
        <div className="sett123-modal-overlay" onClick={closeDeleteModal}>
          <div
            className="sett123-modal-box sett123-confirm-modal"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sett123-confirm-icon">
              <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                <path
                  d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6h16z"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            <h3 className="sett123-confirm-title">Remove this staff member?</h3>
            <p className="sett123-confirm-text">
              This action cannot be undone. Are you sure you want to remove
              this staff member?
            </p>

            <div className="sett123-confirm-footer">
              <button className="sett123-modal-cancel-btn" onClick={closeDeleteModal} disabled={deletingStaff}>
                Cancel
              </button>
              <button className="sett123-btn-delete" onClick={confirmDeleteStaff} disabled={deletingStaff}>
                {deletingStaff ? "Removing..." : "OK, Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- CHANGE PASSWORD MODAL (UI-only) ---------------- */}
      {showPasswordModal && (
        <div className="sett123-modal-overlay" onClick={closePasswordModal}>
          <div className="sett123-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="sett123-modal-header">
              <h3>Change Password</h3>
              <button className="sett123-modal-close-btn" onClick={closePasswordModal}>
                &times;
              </button>
            </div>

            <form onSubmit={handlePasswordSubmit}>
              <div className="sett123-modal-body">
                <div className="sett123-form-group">
                  <label>Current Password *</label>
                  <input
                    type="password"
                    name="current"
                    placeholder="Enter current password"
                    value={passwordForm.current}
                    onChange={handlePasswordChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>New Password *</label>
                  <input
                    type="password"
                    name="newPass"
                    placeholder="Enter new password"
                    value={passwordForm.newPass}
                    onChange={handlePasswordChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Confirm New Password *</label>
                  <input
                    type="password"
                    name="confirm"
                    placeholder="Re-enter new password"
                    value={passwordForm.confirm}
                    onChange={handlePasswordChange}
                  />
                </div>
              </div>

              <div className="sett123-modal-footer">
                <button type="button" className="sett123-modal-cancel-btn" onClick={closePasswordModal}>
                  Cancel
                </button>
                <button type="submit" className="sett123-modal-submit-btn">
                  Update Password
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ---------------- EDIT CONSTITUENCY INFO MODAL ---------------- */}
      {showConstModal && constForm && (
        <div className="sett123-modal-overlay" onClick={closeConstModal}>
          <div className="sett123-modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="sett123-modal-header">
              <h3>Edit Constituency Information</h3>
              <button className="sett123-modal-close-btn" onClick={closeConstModal}>
                &times;
              </button>
            </div>

            <form onSubmit={handleConstSubmit}>
              <div className="sett123-modal-body">
                <div className="sett123-form-group">
                  <label>Constituency Name</label>
                  <input
                    type="text"
                    name="constituency_name"
                    value={constForm.constituency_name || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Constituency No.</label>
                  <input
                    type="text"
                    name="constituency_no"
                    value={constForm.constituency_no || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>State</label>
                  <input
                    type="text"
                    name="state"
                    value={constForm.state || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>District</label>
                  <input
                    type="text"
                    name="district"
                    value={constForm.district || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Lok Sabha Constituency</label>
                  <input
                    type="text"
                    name="lok_sabha_constituency"
                    value={constForm.lok_sabha_constituency || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Current MLA</label>
                  <input
                    type="text"
                    name="current_mla"
                    value={constForm.current_mla || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Year Established</label>
                  <input
                    type="number"
                    name="year_established"
                    value={constForm.year_established || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Total Mandals</label>
                  <input
                    type="number"
                    name="total_mandals"
                    value={constForm.total_mandals || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Total Villages</label>
                  <input
                    type="number"
                    name="total_villages"
                    value={constForm.total_villages || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Total Population</label>
                  <input
                    type="number"
                    name="total_population"
                    value={constForm.total_population || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Office Address</label>
                  <input
                    type="text"
                    name="office_address"
                    value={constForm.office_address || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Contact Email</label>
                  <input
                    type="email"
                    name="contact_email"
                    value={constForm.contact_email || ""}
                    onChange={handleConstFormChange}
                  />
                </div>

                <div className="sett123-form-group">
                  <label>Contact Phone</label>
                  <input
                    type="tel"
                    name="contact_phone"
                    value={constForm.contact_phone || ""}
                    onChange={handleConstFormChange}
                  />
                </div>
              </div>

              <div className="sett123-modal-footer">
                <button type="button" className="sett123-modal-cancel-btn" onClick={closeConstModal} disabled={savingConst}>
                  Cancel
                </button>
                <button type="submit" className="sett123-modal-submit-btn" disabled={savingConst}>
                  {savingConst ? "Saving..." : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SettingsPage;